// core/eval.ts - hold'em hand evaluation. Wraps @poker-apprentice/hand-evaluator (MIT).
// ENGINE_DESIGN.md §1, §5.
//
// Public library API used: `evaluateHoldem({ holeCards, communityCards })` → best 5-card hand
// (an `EvaluatedHand` = { strength, hand }), and `compare(a, b)`. The library takes card STRINGS,
// so we convert CardIndex[] at the boundary via cardToString.
//
// Comparison sign: the library's `compare(a, b)` sorts the STRONGER hand first - it returns -1 when
// `a` is stronger, +1 when `b` is stronger, 0 on a tie (verified against the bundled source and by
// probe). compareHands below flips that to a NATURAL comparator (+1 ⇒ `a` wins) so callers never
// have to remember the inverted sign.
//
// `HandStrength` is not re-exported by hand-evaluator (it lives in @poker-apprentice/types, which
// isn't a direct dependency here), so we expose our own `HandCategory` mirroring its numeric values
// (0..9). `EvaluatedHand.strength` is that same numeric enum at runtime, so the constants compare
// directly.
import { evaluateHoldem, compare } from '@poker-apprentice/hand-evaluator';
import { cardToString } from './cards';
/** Hand-strength categories, mirroring the library's numeric HandStrength enum values. */
export const HandCategory = {
    HighCard: 0,
    OnePair: 1,
    TwoPair: 2,
    ThreeOfAKind: 3,
    Straight: 4,
    Flush: 5,
    FullHouse: 6,
    FourOfAKind: 7,
    StraightFlush: 8,
    RoyalFlush: 9,
};
/** Best 5-card hold'em hand from hole cards + board (0–5 community cards). */
export const bestHand = (hole, board = []) => {
    if (hole.length < 1 || hole.length > 2) {
        throw new RangeError(`hold'em hole cards must number 1–2, got ${hole.length}`);
    }
    if (board.length > 5) {
        throw new RangeError(`board must be 0–5 cards, got ${board.length}`);
    }
    return evaluateHoldem({
        holeCards: hole.map(cardToString),
        communityCards: board.map(cardToString),
    });
};
/** Category (HandCategory value) of the best hand for hole + board. */
export const handCategory = (hole, board = []) => bestHand(hole, board).strength;
/** Compare two already-evaluated hands. +1 if `a` wins, -1 if `b` wins, 0 on a tie. */
export const compareHands = (a, b) => {
    const r = compare(a, b); // -1 ⇒ a stronger
    return r === 0 ? 0 : r === -1 ? 1 : -1;
};
/**
 * Indices of the winning hand(s) among already-evaluated hands; ties return multiple indices
 * (for split pots). Empty input → empty result.
 */
export const winners = (hands) => {
    if (hands.length === 0)
        return [];
    let best = [0];
    for (let i = 1; i < hands.length; i++) {
        const r = compare(hands[i], hands[best[0]]); // -1 ⇒ hands[i] stronger
        if (r === -1)
            best = [i];
        else if (r === 0)
            best.push(i);
    }
    return best;
};
/** Showdown over a shared board: evaluate each player's hole cards and return the winner index(es). */
export const showdown = (holes, board) => winners(holes.map((h) => bestHand(h, board)));
//# sourceMappingURL=eval.js.map